import { memo } from "react";
import { ChevronRight } from "lucide-react";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@fox1ck-org/ui";
import { parseAnsi } from "./ansi";
import {
  formatLogTime,
  formatLogTimeAbsolute,
  getLevelStyle,
  shortenPod,
} from "./levelStyles";
import type { LogRecord } from "./types";

interface LogLineProps {
  record: LogRecord;
  expanded: boolean;
  onToggleExpand: () => void;
  onSelectPod?: (pod: string) => void;
  onSelectContainer?: (container: string) => void;
  searchTerm: string;
  showPod: boolean;
}

/**
 * Highlights case-insensitive matches of `term` inside a plain-text segment
 * without touching ANSI styling by splitting around the match.
 */
function highlight(text: string, term: string): React.ReactNode {
  if (!term) return text;
  const lower = text.toLowerCase();
  const needle = term.toLowerCase();
  const out: React.ReactNode[] = [];
  let idx = 0;
  let match = lower.indexOf(needle, idx);
  while (match !== -1) {
    if (match > idx) out.push(text.slice(idx, match));
    out.push(
      <mark
        key={`m-${match}`}
        className="bg-yellow-400/30 text-yellow-100 rounded px-0.5"
      >
        {text.slice(match, match + term.length)}
      </mark>
    );
    idx = match + term.length;
    match = lower.indexOf(needle, idx);
  }
  if (idx < text.length) out.push(text.slice(idx));
  return out;
}

function LogLineInner({
  record,
  expanded,
  onToggleExpand,
  onSelectPod,
  onSelectContainer,
  searchTerm,
  showPod,
}: LogLineProps) {
  const level = (record.level || "INFO").toUpperCase();
  const style = getLevelStyle(level);
  const segments = parseAnsi(record.msg);
  const hasExpandableDetails =
    record.correlationId ||
    (record.context && Object.keys(record.context).length > 0) ||
    record.logger;

  return (
    <div
      className={cn(
        "group flex items-start gap-2 px-3 py-0.5 font-mono text-[12px] leading-5 border-l-2 border-b border-b-border/30",
        style.border,
        style.rowBg,
        expanded && "bg-accent/40",
        hasExpandableDetails && "cursor-pointer hover:bg-accent/30"
      )}
      onClick={hasExpandableDetails ? onToggleExpand : undefined}
    >
      {/* Expand chevron (reserved slot so non-expandable rows stay aligned) */}
      <div className="w-3 flex-shrink-0 pt-0.5">
        {hasExpandableDetails && (
          <ChevronRight
            className={cn(
              "h-3 w-3 text-muted-foreground transition-transform",
              expanded && "rotate-90"
            )}
          />
        )}
      </div>

      {/* Timestamp */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="w-[84px] flex-shrink-0 text-muted-foreground tabular-nums">
            {formatLogTime(record.ts)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="font-mono text-xs">
          {formatLogTimeAbsolute(record.ts)}
        </TooltipContent>
      </Tooltip>

      {/* Level pill */}
      <span
        className={cn(
          "w-[52px] flex-shrink-0 text-center border rounded text-[10px] font-semibold uppercase leading-[18px] tracking-wide",
          style.pill
        )}
      >
        {level.slice(0, 5)}
      </span>

      {/* Pod · container chip */}
      {showPod && record.pod && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelectPod?.(record.pod);
          }}
          className="flex-shrink-0 text-[10px] px-1.5 leading-[18px] rounded bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors max-w-[140px] truncate"
          title={record.pod + (record.container ? ` · ${record.container}` : "")}
        >
          {shortenPod(record.pod)}
          {record.container && (
            <span
              className="text-muted-foreground/60 hover:text-foreground/60"
              onClick={(e) => {
                e.stopPropagation();
                onSelectContainer?.(record.container!);
              }}
            >
              ·{record.container}
            </span>
          )}
        </button>
      )}

      {/* Message */}
      <div className="flex-1 min-w-0 text-foreground/90 break-all whitespace-pre-wrap">
        {segments.map((seg, i) => (
          <span key={i} className={seg.className}>
            {highlight(seg.text, searchTerm)}
          </span>
        ))}
      </div>
    </div>
  );
}

export const LogLine = memo(LogLineInner);
